import { Money, type DecimalString, type InvoiceOrBillOut } from "@shop-ledger/shared-types";

export type AgingBucketKey = "CURRENT" | "DAYS_1_30" | "DAYS_31_60" | "DAYS_61_90" | "DAYS_90_PLUS";

export const AGING_BUCKET_ORDER: readonly AgingBucketKey[] = [
  "CURRENT",
  "DAYS_1_30",
  "DAYS_31_60",
  "DAYS_61_90",
  "DAYS_90_PLUS",
];

export const AGING_BUCKET_LABEL: Record<AgingBucketKey, string> = {
  CURRENT: "Current (not yet due)",
  DAYS_1_30: "1–30 days overdue",
  DAYS_31_60: "31–60 days overdue",
  DAYS_61_90: "61–90 days overdue",
  DAYS_90_PLUS: "90+ days overdue",
};

export interface AgedInvoice {
  invoice: InvoiceOrBillOut;
  /** Whole days between `today` and `due_date`; 0 or negative means not yet due. */
  daysPastDue: number;
  bucket: AgingBucketKey;
  /** `amount_due - amount_paid`, i.e. what's still outstanding on this invoice/bill. */
  balanceRemaining: DecimalString;
}

/** Normalize a `Date` to UTC midnight so day-difference math isn't sensitive to the
 * caller's local timezone or time-of-day (`due_date` is an `ISODateString`, i.e. a bare
 * `YYYY-MM-DD` with no time/timezone component of its own). */
function toUtcMidnight(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Buckets one invoice/bill by `due_date` vs. `today`. This is a **client-side derived
 * view** — `backend/BACKEND_ARCHITECTURE.md`'s API surface table has no dedicated aging
 * endpoint, and `InvoiceOrBill.status` can say `OVERDUE` but no backend job actually
 * transitions a row into that state yet (`shared-types`' `InvoiceOrBillStatus` doc
 * comment: "no scheduled job exists yet"). This function is what actually computes
 * "overdue," from real `due_date`/`amount_due`/`amount_paid` data, independent of whatever
 * `status` string happens to be stored.
 */
export function computeAgingBucket(dueDate: string, today: Date = new Date()): { daysPastDue: number; bucket: AgingBucketKey } {
  const dueMs = toUtcMidnight(new Date(`${dueDate}T00:00:00Z`));
  const todayMs = toUtcMidnight(today);
  const daysPastDue = Math.round((todayMs - dueMs) / MS_PER_DAY);

  let bucket: AgingBucketKey;
  if (daysPastDue <= 0) bucket = "CURRENT";
  else if (daysPastDue <= 30) bucket = "DAYS_1_30";
  else if (daysPastDue <= 60) bucket = "DAYS_31_60";
  else if (daysPastDue <= 90) bucket = "DAYS_61_90";
  else bucket = "DAYS_90_PLUS";

  return { daysPastDue, bucket };
}

/**
 * Ages every invoice/bill with an outstanding balance (`amount_due > amount_paid` — a
 * fully-paid row, or a zero-amount row, has nothing to age) into a per-row bucket. Sorted
 * by nothing in particular here; the consuming page applies its own `DataTable`
 * sort/filter on top.
 */
export function ageInvoices(invoices: readonly InvoiceOrBillOut[], today: Date = new Date()): AgedInvoice[] {
  return invoices
    .filter((invoice) => Money.compare(invoice.amount_due, invoice.amount_paid) > 0)
    .map((invoice) => {
      const balanceRemaining = Money.subtract(invoice.amount_due, invoice.amount_paid);
      const { daysPastDue, bucket } = computeAgingBucket(invoice.due_date, today);
      return { invoice, daysPastDue, bucket, balanceRemaining };
    });
}

/** Per-bucket outstanding-balance totals — the "aging matrix" summary row (§5). */
export function summarizeAgingBuckets(aged: readonly AgedInvoice[]): Record<AgingBucketKey, DecimalString> {
  const totals = Object.fromEntries(AGING_BUCKET_ORDER.map((key) => [key, Money.zero()])) as Record<
    AgingBucketKey,
    DecimalString
  >;
  for (const item of aged) {
    totals[item.bucket] = Money.add(totals[item.bucket], item.balanceRemaining);
  }
  return totals;
}
